import { el } from '@/utils/dom';

export type DocNodeTypes = 'subtitle' | 'paragraph' | 'example' | 'reference' | 'simple-paragraph' | 'strong';
export type DocNodes = {
  type: DocNodeTypes;
  children?: DocNodes[];
} & (| HtmlText | SimpleText);

export type TextDocNode = Extract<DocNodes, { text: string; }>;
export type HtmlDocNode = Extract<DocNodes, { html: string; }>;

export function isHtmlDocNode(node: DocNodes): node is HtmlDocNode {
  return 'html' in node;
}
export function isTextDocNode(node: DocNodes): node is TextDocNode {
  return 'text' in node;
}

export interface HtmlText {
  html: string;
}
export interface SimpleText {
  text: string;
}


interface AcronymDef {
  abbr: string;
  full: string;
  explain: DocNodes[] | string;
  example?: DocNodes[] | string;
}

const ACRONYMS: AcronymDef[] = [
  {
    abbr: 'PMS',
    full: 'Property Management System',
    explain: [
      {
        type: 'paragraph',
        text: 'Il software che gestisce le operazioni quotidiane di un hotel: prenotazioni, check-in/out, housekeeping, fatturazione, disponibilità stanze.',
      }
    ],
    example: 'Es. Cybhotel, Opera, Mews, Cloudbeds.',
  },
  {
    abbr: 'OTA',
    full: 'Online Travel Agency',
    explain: [
      {
        type: 'paragraph',
        text: 'Piattaforma online di prenotazione (Booking, Expedia, Airbnb). Trattiene una commissione su ogni prenotazione (15-25%).',
      }
    ],
    example: 'Una prenotazione da Booking.com del 20% di commissione su ADR 100€ = 20€ di costo.',
  },
  {
    abbr: 'Channel Manager',
    full: 'Channel Manager',
    explain: [
      {
        type: 'paragraph',
        text: 'Sistema che sincronizza in tempo reale disponibilità e prezzi tra PMS e tutte le OTA, evitando overbooking e aggiornamenti manuali.',
      }
    ],
    example: 'Una stanza venduta su Booking viene immediatamente rimossa da Expedia e Airbnb.',
  },
  {
    abbr: 'Booking Engine',
    full: 'Booking Engine',
    explain: [
      {
        type: 'paragraph',
        text: 'Modulo di prenotazione diretta integrato nel sito dell’hotel. Permette di vendere stanze senza passare dalle OTA, azzerando le commissioni.',
      }
    ],
    example: 'Un cliente prenota su www.tuohotel.it invece che su Booking → 0€ di commissione OTA.',
  },
  {
    abbr: 'ADR',
    full: 'Average Daily Rate',
    explain: [
      {
        type: 'subtitle',
        text: 'Cos’è l’ADR?',
      },
      {
        type: 'paragraph',
        text: `La tariffa media giornaliera (ADR) è un parametro fondamentale nel settore alberghiero, che ti permette di calcolare il tuo guadagno medio per camera (notti in omaggio e servizi extra esclusi).`
      },
      {
        type: 'paragraph',
        text: `La formula della tariffa media giornaliera è un modo semplice per scoprire quanto, in media, gli ospiti pagano per soggiornare in un hotel per una notte. Per calcolare l'ADR, si somma tutto il denaro che l'hotel ha ricavato dalle prenotazioni delle camere in un giorno e lo si divide per il numero di camere prenotate quel giorno.`,
      },
      {
        type: 'paragraph',
        text: `Le tendenze stagionali influenzano l'ADR. Una maggiore domanda nelle stagioni di punta può aumentare le tariffe, mentre i periodi di bassa stagione possono richiedere prezzi promozionali. Un confronto anno su anno dei rapporti sui ricavi è necessario per una visione equilibrata delle prestazioni dell'ADR.`,
      },
      {
        type: 'paragraph',
        text: `Valutare il tuo attuale successo finanziario e monitorare le tue prestazioni rispetto agli obiettivi mensili e annuali può essere raggiunto utilizzando l'ADR. Monitorare il successo dell'hotel in vari periodi è vitale per stabilire proiezioni accurate e gestire l'hotel in modo efficace.`,
      }
    ],
    example: [
      {
        type: 'reference',
        text: '',
        children: [
          {
            type: 'simple-paragraph',
            text: `l'ADR include solo i ricavi dalle prenotazioni delle camere ed esclude altre fonti come cibo, bevande e strutture per conferenze. Questo può distorcere il ricavo complessivo dell'hotel con flussi di entrate significative oltre le prenotazioni delle camere.`
          }, {
            type: 'example',
            text: `Ricavi camere 10.000€ / 100 camere vendute = ADR 100€.`

          }
        ]
      }
    ]
  },
  {
    abbr: 'TRevPAR',
    full: 'Total Revenue Per Available Room',
    explain: [
      {
        type: 'paragraph',
        text: 'Ricavo totale (camere + F&B + extra) per camera disponibile. Più completo del RevPAR perché include tutte le fonti di ricavo.'
      }
    ],
    example: 'Ricavi totali 15.000€ / 120 camere disponibili = TRevPAR 125€.',
  },
  {
    abbr: 'RevPAR',
    full: 'Revenue Per Available Room',
    explain: [
      {
        type: 'paragraph',
        html: '<strong>Il RevPAR è un indicatore di performance che comprende tutte le fonti di revenue e ti fornisce informazioni utili per affinare la tua strategia.</strong>'
      },
      {
        type: 'subtitle',
        text: 'Cos’è il RevPAR?'
      },
      {
        type: 'paragraph',
        text: `Il RevPAR, acronimo di Revenue per Available Room (revenue per camera disponibile), è un parametro comunemente utilizzato nel settore dell'ospitalità.`
      },
      {
        type: 'paragraph',
        text: `Il RevPAR misura il revenue medio generato da ciascuna camera e si calcola suddividendo il revenue totale per il numero di camere disponibili.`
      },
    ],
    example: [
      {
        type: 'reference',
        text: '',
        children: [
          {
            type: 'simple-paragraph',
            text: `Ecco la formula per calcolare il RevPAR del tuo hotel: RevPAR = Ricavo Totale Camere / Numero Totale di Camere Disponibili`
          },
          {
            type: 'example',
            text: `Se il tuo hotel ad esempio ha generato 1.200€ di revenue per camera e ha 12 camere, il RevPAR sarà pari a 1.200€/ 12 = 100€.`
          },
          {
            type: 'simple-paragraph',
            text: `Ciò significa che l'hotel genera un revenue di 120€ al giorno per ogni camera disponibile.`
          },
          {
            type: 'simple-paragraph',
            text: `Se il RevPAR è più alto significa che l'hotel sta sfruttando al massimo il revenue dalle proprie camere disponibili, e ciò è direttamente influenzato dall'occupazione.`
          },
          {
            type: 'simple-paragraph',
            text: `Monitorando questa metrica nel tempo, gli albergatori sono in grado di regolare al meglio i prezzi e i tassi di occupazione, ottimizzando così al massimo il proprio revenue.`
          },
        ]
      }
    ]
  },
  {
    abbr: 'F&B',
    full: 'Food & Beverage',
    explain: [
      {
        type: 'paragraph',
        text: 'Tutti i ricavi legati a cibo e bevande: colazione, ristorante, bar, room service, banchetti.'
      }
    ],
    example: 'Colazione inclusa a 15€/coperto × 50 coperti/giorno = 750€/giorno di F&B.',
  },
  {
    abbr: 'CapEx',
    full: 'Capital Expenditure',
    explain: [
      {
        type: 'paragraph',
        text: 'Spesa in conto capitale (investimenti pluriennali). Beni che vengono ammortizzati su più anni: arredi, ristrutturazioni, hardware.'
      }
    ],
    example: 'Acquisto di 10 totem check-in da 5.000€ cad. = 50.000€ CapEx ammortizzati in 5 anni.',
  },
  {
    abbr: 'OpEx',
    full: 'Operating Expenditure',
    explain: [
      {
        type: 'paragraph',
        text: 'Spesa operativa corrente: stipendi, utenze, licenze software, manutenzioni. Si imputano interamente all’anno in cui sono sostenute.'
      }
    ],
    example: 'Abbonamento annuo al PMS di 6.000€ = 6.000€ OpEx dell’anno.',
  },
  {
    abbr: 'ROI',
    full: 'Return On Investment',
    explain: [
      {
        type: 'paragraph',
        text: 'Indice di rendimento di un investimento. Nel calcolatore viene stimato tramite il payback period: in quanti mesi il risparmio ripaga il costo del sistema.'
      }
    ],
    example: 'Costo sistema 12.000€ / risparmio netto 1.000€ al mese = payback 12 mesi.',
  },
  {
    abbr: 'Payback',
    full: 'Payback Period',
    explain: [
      {
        type: 'paragraph',
        text: 'Tempo necessario per recuperare il capitale investito. Più breve = investimento più sicuro.'
      }
    ],
    example: 'Payback di 8 mesi significa che dopo 8 mesi il sistema è "regalato" e gli anni successivi sono puro guadagno.',
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Come viene calcolato il risparmio sulle ore?',
    a: 'Moltiplichiamo: stanze × occupazione × ore manuali risparmiate per stanza/giorno × giorni/anno × costo orario. Rappresenta il valore del lavoro che non va più fatto a mano (inserimento prenotazioni, check-in manuale, fatturazione, sync OTA).',
  },
  {
    q: 'Da dove deriva l’incremento di occupazione?',
    a: 'Un sistema integrato con channel manager + booking engine migliora la visibilità online, riduce gli overbooking e permette di vendere su più canali in tempo reale. Il ΔO (tipicamente 3-8%) rappresenta l’incremento di occupazione atteso nel primo anno.',
  },
  {
    q: 'Cos’è l’incremento di TRevPAR?',
    a: 'Oltre alle camere, un buon PMS permette di monetizzare servizi extra (F&B, spa, upgrade, esperienze) con upselling automatico. Il campo opzionale % di incremento TRevPAR stima questo effetto addizionale.',
  },
  {
    q: 'Il costo del sistema è CapEx o OpEx?',
    a: 'Nel calcolatore consideriamo il costo annuo ricorrente (OpEx). Se si fa un investimento hardware iniziale (es. totem), andrebbe distribuito sugli anni di vita utile e sommato al canone SaaS annuo.',
  },
  {
    q: 'Il payback period è affidabile?',
    a: 'È una stima basata su parametri medi. Per scenari reali consiglia di usare i tre livelli (conservativo / realistico / aggressivo) e prendere come riferimento l’intervallo, non un singolo numero.',
  },
  {
    q: 'I dati inseriti vengono salvati?',
    a: 'Sì, in localStorage del browser (nessun server). Sono associati solo al dispositivo. Dalla pagina Impostazioni puoi esportarli in CSV, importarli o resettarli.',
  },
];

export class LegendView {
  mount(root: HTMLElement): void {
    root.innerHTML = '';
    root.setAttribute('aria-label', 'Legenda e FAQ');

    const intro = el('section', { className: 'mb-8 animate-fade-in' }, [
      el('h1', { className: 'text-3xl font-bold text-slate-900', text: 'Legenda e spiegazioni' }),
      el('p', { className: 'mt-2 text-slate-600 max-w-3xl', text: 'Tutti gli acronimi tecnici usati nel calcolatore, spiegati in linguaggio semplice con esempi pratici. Più sotto, le risposte alle domande più frequenti.' }),
    ]);

    // Acronimi
    const acrSection = el('section', { className: 'mb-12' }, [
      el('h2', { className: 'text-xl font-semibold text-slate-900 mb-4', text: 'Acronimi e termini' }),
      el('div', { className: 'grid gap-4 sm:grid-cols-2' }, ACRONYMS.map((a) => this.acronymCard(a))),
    ]);

    // FAQ
    const faqSection = el('section', { className: 'mb-8' }, [
      el('h2', { className: 'text-xl font-semibold text-slate-900 mb-4', text: 'Domande frequenti' }),
      el('div', { className: 'space-y-3' }, FAQ.map((f) => this.faqItem(f))),
    ]);

    root.appendChild(intro);
    root.appendChild(acrSection);
    root.appendChild(faqSection);
  }

  private acronymCard(a: AcronymDef): HTMLElement {
    const example = this.renderSection(a.example);
    const explain = this.renderSection(a.explain);

    const children: (HTMLElement | string)[] = [
      el('div', { className: 'flex items-center justify-between mb-2' }, [
        el('h3', { className: 'text-base font-bold text-brand-700', text: a.abbr }),
        el('span', { className: 'text-xs text-slate-500', text: a.full }),
      ]),

    ];

    if (explain) {
      if (Array.isArray(explain)) {
        children.push(...explain);
      } else {
        children.push(explain);
      }
    }

    if (example) {
      if (Array.isArray(example)) {
        children.push(...example);
      } else {
        children.push(example);
      }
    }

    return el('article', { className: 'card card-hover' }, children);
  }

  private renderSection(section?: DocNodes[] | string): HTMLElement | HTMLElement[] | null {
    if (!section) return null;
    if (typeof section === 'string') {
      return el('p', { className: 'mt-3 text-xs text-slate-500 border-l-2 border-brand-200 pl-3 italic', text: `Esempio: ${section}` })
    }
    return this.renderDocNodes(section);
  }

  private faqItem(f: { q: string; a: string }): HTMLElement {
    const details = el('details', { className: 'card !p-0 overflow-hidden group' });
    const summary = el('summary', {
      className: 'cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition font-medium text-slate-800',
    }, [
      el('span', { text: f.q }),
      el('i', { className: 'fas fa-chevron-down text-brand-600 transition-transform group-open:rotate-180' }),
    ]);
    const body = el('div', { className: 'px-5 pb-5 text-sm text-slate-600 leading-relaxed' }, [
      el('p', { text: f.a }),
    ]);
    details.appendChild(summary);
    details.appendChild(body);
    return details;
  }

  private renderDocNodes(nodes: DocNodes[]): HTMLElement[] {
    return nodes.map((n) => {

      var textOrHtml = isHtmlDocNode(n) ? { html: n.html } : { text: n.text };


      switch (n.type) {
        case 'subtitle':
          return el('h4',
            {
              className: 'text-sm font-semibold text-slate-800 mt-3 mb-1',
              ...textOrHtml
            },
            n.children ? this.renderDocNodes(n.children) : undefined
          );
        case 'reference':
          return el('div', { className: 'mt-3 text-xs text-slate-500 border-l-2 border-brand-200 pl-3 italic' }, n.children ? this.renderDocNodes(n.children) : undefined);
        case 'example':
          for (const prop in textOrHtml) {
            const key = prop as keyof typeof textOrHtml;
            textOrHtml[key] = `Esempio: ${textOrHtml[key]}`;
          }
          return el('p', { className: '', ...textOrHtml }, n.children ? this.renderDocNodes(n.children) : undefined);
        case 'simple-paragraph':
          return el('p', { ...textOrHtml }, n.children ? this.renderDocNodes(n.children) : undefined);
        case 'paragraph':
        default:
          return el('p', { className: 'text-sm text-slate-700 leading-relaxed', ...textOrHtml }, n.children ? this.renderDocNodes(n.children) : undefined);
      }
    });
  }
}
